import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { useAnchor } from "hooks";
import { TickIcon2 } from "icons/TickIcon2";
import { Features22Content } from "types/graphql/generated";

interface Props {
  content: Features22Content;
}

const Features22: VFC<Props> = withErrorBoundary(({ content: { title, description, list } }) => {
  useAnchor("features22");

  return (
    <StyledFeatures22 className="features22 Content">
      <ul className="Features22__features">
        {list.map(({ element, id }) => (
          <li key={id} className="Features22__feature Font_40_48_600 lg:Font_24_32_600">
            <TickIcon2 className="Features22__tickIcon" />
            <p className="Features22__element">{element}</p>
          </li>
        ))}
      </ul>
      <div className="Features22__mainContent">
        <h3 className="Features22__title Font_24_32_600">{title}</h3>
        <Markdown className="Features22__description Color_secondary Font_24_32_450">{description}</Markdown>
      </div>
    </StyledFeatures22>
  );
}, {});

const StyledFeatures22 = styled.section`
  display: flex;
  align-items: flex-end;
  margin-top: -64px;
  padding-top: 64px;

  .Features22__features {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
  }

  .Features22__feature {
    width: fit-content;
    display: flex;
    align-items: center;
    padding: 8px 32px 8px 30px;
    border-radius: 64px;
  }

  .Features22__feature:not(:first-child) {
    margin-top: 24px;
  }

  .Features22__feature:nth-child(3n + 1) {
    background: ${({ theme }) => theme.colors.coral["40"]};
    color: ${({ theme }) => theme.colors.coral["80"]};

    svg {
      stroke: ${({ theme }) => theme.colors.coral["80"]};
    }
  }

  .Features22__feature:nth-child(3n + 2) {
    background: ${({ theme }) => theme.colors.eucalyptus["30"]};
    color: ${({ theme }) => theme.colors.eucalyptus["80"]};

    svg {
      stroke: ${({ theme }) => theme.colors.eucalyptus["80"]};
    }
  }

  .Features22__feature:nth-child(3n + 3) {
    background: ${({ theme }) => theme.colors.dusk["20"]};
    color: ${({ theme }) => theme.colors.dusk["80"]};

    svg {
      stroke: ${({ theme }) => theme.colors.dusk["80"]};
    }
  }

  .Features22__tickIcon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .Features22__element {
    margin-left: 12px;
  }

  .Features22__mainContent {
    display: flex;
    flex-direction: column;
    margin-left: 120px;
  }

  .Features22__description {
    margin-top: 8px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    align-items: flex-start;

    .Features22__feature:not(:first-child) {
      margin-top: 16px;
    }

    .Features22__mainContent {
      margin-left: 48px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    flex-direction: column;

    .Features22__features {
      align-items: flex-start;
    }

    .Features22__mainContent {
      margin-left: 0;
      margin-top: 48px;
    }
  }
`;

gql`
  fragment Features22Content on ComponentFeaturesFeatures22 {
    title
    description
    list {
      id
      element
    }
  }
`;

export { Features22 };
export type { Props as Features22Props };
